import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { parseDuration, formatDuration } from "@/lib/duration";
import { toast } from "sonner";
import { Trash2, AlertOctagon, Bell, TrendingUp } from "lucide-react";
import TaskCommentThread from "@/components/plan/TaskCommentThread";
import { overshootStatus } from "@/components/plan/dueDateStatus";
import { nudgeApi, launchpadApi } from "@/lib/api";
import TimePicker from "@/components/TimePicker";
import { formatTime12 } from "@/lib/time";

const PHASES = [
  { value: "pre",  label: "Pre-Production",  hint: "Cascade by default" },
  { value: "prod", label: "Production",       hint: "Locked — synced to shoot days" },
  { value: "post", label: "Post-Production", hint: "Cascade by default" },
];

const KANBAN_OPTIONS = [
  { value: "todo",        label: "To Do" },
  { value: "in_progress", label: "In Progress" },
  { value: "review",      label: "Review" },
  { value: "done",        label: "Done" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

// Compact relative-time formatter — "3h ago", "2d ago", "just now".
// Used by the nudge history line; kept local so we don't drag in a big
// dep just for one string.
function formatRelativeTime(dt) {
  const now = new Date();
  const diffSec = Math.max(0, Math.round((now.getTime() - dt.getTime()) / 1000));
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  const days = Math.round(diffSec / 86400);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return `${months}mo ago`;
}

export default function PlanTaskDialog({ open, onOpenChange, task, projectId, canEdit = true, defaultPhase = "pre", allTasks = [], dependencies = [], assignableUsers = [], onSave, onDelete, onCreateDependency, onDeleteDependency }) {
  const isEdit = !!task?.id;
  const isProdLinked = !!task?.call_sheet_id && task?.phase === "prod";

  const [form, setForm] = useState(emptyForm(defaultPhase));
  const [durationInput, setDurationInput] = useState("1d");
  const [saving, setSaving] = useState(false);
  // Pending predecessors — only used in CREATE mode. Once the task exists,
  // dependencies are managed via the API-backed DependencySection below.
  // Shape: [{ from_id, type }]
  const [pendingPredecessors, setPendingPredecessors] = useState([]);
  // Nudge history — small "Nudged Nx" hint. Fetched once per open on existing tasks.
  const [nudgeHistory, setNudgeHistory] = useState(null);
  // Capacity check for the selected assignee — populates when the picker changes.
  const [assigneeCapacity, setAssigneeCapacity] = useState(null);
  const [capacityLoading, setCapacityLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        name: task.name || "",
        phase: task.phase || defaultPhase,
        start: task.start || todayISO(),
        start_time: task.start_time || "",
        duration: task.duration ?? 1,
        is_milestone: !!task.is_milestone,
        schedule_mode: task.schedule_mode || "cascade",
        kanban_status: task.kanban_status || "todo",
        percent_complete: task.percent_complete ?? 0,
        notes: task.notes || "",
        assigned_user_id: task.assigned_user_id || "",
        due_date: task.due_date || "",
        due_time: task.due_time || "",
        is_recurring: !!task.is_recurring,
        recurrence_type: task.recurrence_type || null,
        recurrence_end_date: task.recurrence_end_date || "",
      });
      setDurationInput(formatDuration(task.duration ?? 1) || "1d");
    } else {
      setForm(emptyForm(defaultPhase));
      setDurationInput("1d");
    }
    setNudgeHistory(null);
    setAssigneeCapacity(null);
    setPendingPredecessors([]);
  }, [open, task, defaultPhase]);

  // Load nudge history once when opening an existing task. Guarded by task?.id
  // so we don't ping the API for the "New task" case.
  useEffect(() => {
    if (!open || !task?.id) return;
    let cancelled = false;
    nudgeApi
      .history(task.id)
      .then((d) => { if (!cancelled) setNudgeHistory(d); })
      .catch(() => { if (!cancelled) setNudgeHistory({ count: 0, entries: [] }); });
    return () => { cancelled = true; };
  }, [open, task?.id]);

  // Capacity-aware assignment warning: whenever the assignee changes to a real
  // user, fetch that user's this-week utilization. Debounced 300ms so a picker
  // scrub doesn't spam the API. Empty assignee → clear capacity readout.
  useEffect(() => {
    const uid = form.assigned_user_id;
    if (!open || !uid) {
      setAssigneeCapacity(null);
      return;
    }
    let cancelled = false;
    setCapacityLoading(true);
    const timer = setTimeout(() => {
      launchpadApi
        .getTeamCapacity({ userId: uid })
        .then((d) => {
          if (cancelled) return;
          const row = (d.users || []).find((u) => u.user_id === uid);
          setAssigneeCapacity(
            row
              ? { ...row, week_capacity: d.week_capacity, hours_per_day: d.hours_per_day }
              : { user_id: uid, scheduled_hours: 0, capacity_hours: d.week_capacity, utilization: 0, name: "" }
          );
        })
        .catch(() => { if (!cancelled) setAssigneeCapacity(null); })
        .finally(() => { if (!cancelled) setCapacityLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, form.assigned_user_id]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canEdit) return; // viewer-mode: never submit, comments-only
    const name = (form.name || "").trim();
    if (!name) {
      toast.error("Task name is required");
      return;
    }
    if (!form.start) {
      toast.error("Start date is required");
      return;
    }
    const duration = parseDuration(durationInput);
    if (!duration) {
      toast.error("Duration — try '3d' or '4h'");
      return;
    }
    setSaving(true);
    try {
      // start_time: "" clears; a valid HH:MM sets. Trim to satisfy backend
      // validation which expects HH:MM with a leading zero.
      const rawStartTime = (form.start_time || "").trim();
      const normalizedStartTime = rawStartTime
        ? (rawStartTime.length === 4 ? `0${rawStartTime}` : rawStartTime)
        : "";
      await onSave({
        ...form,
        name,
        duration,
        is_milestone: !!form.is_milestone,
        start_time: normalizedStartTime,
        // Predecessors only travel with CREATE. When editing, the
        // API-backed DependencySection has already persisted changes.
        ...(!isEdit && pendingPredecessors.length > 0
          ? { predecessors: pendingPredecessors }
          : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="plan-task-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            {isEdit ? "Edit Task" : "New Task"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {isProdLinked
              ? "This Production block is auto-synced from a call sheet day. Editing the start date will move the call sheet too."
              : "Choose a phase, set duration in days or hours, and lock/cascade behavior."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-4">
          {/* Wrap task-edit fields in a fieldset so they can be wholesale
              disabled for viewers. The comment thread + dialog footer live
              OUTSIDE this fieldset so they remain interactive. */}
          <fieldset
            disabled={!canEdit}
            className={`contents ${!canEdit ? "opacity-70" : ""}`}
          >
          {/* Tier 1 — Mark complete checkbox, prominent at top. */}
          <label
            className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer select-none transition-colors ${
              form.kanban_status === "done"
                ? "border-emerald-700/60 bg-emerald-950/30"
                : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
            }`}
          >
            <input
              type="checkbox"
              data-testid="task-complete-checkbox"
              checked={form.kanban_status === "done"}
              onChange={(e) =>
                setForm({
                  ...form,
                  kanban_status: e.target.checked ? "done" : "todo",
                  // Keep % in sync with the checkbox — matches the
                  // "mark complete outside the dialog" behavior so users
                  // don't have to touch both fields.
                  // Checking → jump to 100. Un-checking a task that was
                  // at 100 → drop to 0 (was implicitly "just complete");
                  // if % is already below 100 we leave it alone so
                  // in-progress work isn't lost when someone toggles the
                  // checkbox off by mistake.
                  percent_complete: e.target.checked
                    ? 100
                    : form.percent_complete === 100
                    ? 0
                    : form.percent_complete,
                })
              }
              className="w-4 h-4 accent-emerald-500"
            />
            <span
              className={`text-sm ${
                form.kanban_status === "done" ? "text-emerald-300" : "text-zinc-300"
              }`}
            >
              {form.kanban_status === "done" ? "Marked complete" : "Mark complete"}
            </span>
            {form.kanban_status === "done" && (
              <span className="ml-auto text-[10px] font-mono text-emerald-500/70">
                ✓ done
              </span>
            )}
          </label>

          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">
              Task Name<span className="text-amber-500 ml-1">*</span>
            </Label>
            <Input
              data-testid="task-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Storyboard Review"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              autoFocus
              disabled={isProdLinked}
            />
          </div>

          {/* Nudge history hint — subtle, one-line audit trail. Only shows
              when the task has actually been nudged; silent otherwise so it
              doesn't add UI weight to healthy tasks. */}
          {isEdit && nudgeHistory && nudgeHistory.count > 0 && (() => {
            const last = nudgeHistory.entries[0];
            const sentAt = last?.sent_at ? new Date(last.sent_at) : null;
            const agoText = sentAt
              ? formatRelativeTime(sentAt)
              : "recently";
            return (
              <div
                data-testid="nudge-history-line"
                className="flex items-center gap-2 text-[11px] text-zinc-500 -mt-1"
              >
                <Bell className="w-3 h-3 text-amber-500/70 shrink-0" />
                <span>
                  Nudged{" "}
                  <span className="text-zinc-300 font-mono">
                    {nudgeHistory.count}x
                  </span>
                  {last && (
                    <>
                      {" · last "}
                      <span className="text-zinc-300">{agoText}</span>
                      {" by "}
                      <span className="text-zinc-300">{last.sender_name}</span>
                    </>
                  )}
                </span>
              </div>
            );
          })()}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Phase</Label>
              <Select
                value={form.phase}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    phase: v,
                    // When switching phase manually, also flip default schedule mode
                    schedule_mode: form.schedule_mode === "locked" || form.schedule_mode === "cascade"
                      ? (v === "prod" ? "locked" : "cascade")
                      : form.schedule_mode,
                  })
                }
                disabled={isProdLinked}
              >
                <SelectTrigger
                  data-testid="task-phase"
                  className="bg-zinc-950 border-zinc-800 focus:ring-amber-500"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {PHASES.map((p) => (
                    <SelectItem key={p.value} value={p.value} className="focus:bg-zinc-900">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Schedule Mode</Label>
              <Select
                value={form.schedule_mode}
                onValueChange={(v) => setForm({ ...form, schedule_mode: v })}
              >
                <SelectTrigger
                  data-testid="task-mode"
                  className="bg-zinc-950 border-zinc-800 focus:ring-amber-500"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="cascade" className="focus:bg-zinc-900">Cascade</SelectItem>
                  <SelectItem value="locked" className="focus:bg-zinc-900">Locked</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-[1.1fr_1fr_0.9fr] gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Start Date</Label>
              <Input
                data-testid="task-start"
                type="date"
                value={form.start}
                onChange={(e) => setForm({ ...form, start: e.target.value })}
                style={{ colorScheme: "dark" }}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 [&::-webkit-calendar-picker-indicator]:invert-[0.6] [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">
                @ Time <span className="text-zinc-600 normal-case">(opt.)</span>
              </Label>
              <TimePicker
                testIdPrefix="task-start-time"
                value={form.start_time || ""}
                onChange={(v) => setForm({ ...form, start_time: v })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">
                Duration <span className="text-zinc-600">(3d, 4h)</span>
              </Label>
              <Input
                data-testid="task-duration"
                value={durationInput}
                onChange={(e) => setDurationInput(e.target.value)}
                placeholder="3d"
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 font-mono"
              />
            </div>
          </div>

          {/* Walker output — Phase-B hour-block spillover. Only shown for
              tasks that opted into hour math via `start_time`. Read-only:
              the walker recomputes end + end_time server-side on save. */}
          {isEdit && task?.start_time && task?.end && (
            <div
              data-testid="task-walker-output"
              className="text-[11px] font-mono text-zinc-500 flex items-center gap-2 -mt-1"
            >
              <span className="text-zinc-600 uppercase tracking-wide text-[10px]">
                Ends
              </span>
              <span className="text-zinc-300">{task.end}</span>
              {task.end_time && (
                <>
                  <span className="text-zinc-700">·</span>
                  <span className="text-zinc-300">{formatTime12(task.end_time)}</span>
                </>
              )}
              <span className="text-zinc-700">·</span>
              <span className="text-zinc-500">
                {task.duration_hours || (task.duration || 0) * 8}h
              </span>
              <span className="text-zinc-600 text-[10px]">
                walking hour-blocks
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">
                Due Date <span className="text-zinc-600">(optional)</span>
              </Label>
              <div className="relative">
                <Input
                  data-testid="task-due-date"
                  type="date"
                  value={form.due_date || ""}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value, due_time: e.target.value ? form.due_time : "" })}
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                />
                {form.due_date && (
                  <button
                    type="button"
                    data-testid="task-due-date-clear"
                    onClick={() => setForm({ ...form, due_date: "", due_time: "" })}
                    className="absolute right-9 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200 text-xs"
                    aria-label="Clear due date"
                  >
                    ×
                  </button>
                )}
              </div>
              <p className="text-[10px] text-zinc-600 leading-tight">
                Drives the ON TIME / LATE badge. Doesn&apos;t move the task — leave blank to skip.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">
                Due Time <span className="text-zinc-600 normal-case">(Central, opt.)</span>
              </Label>
              <div className={!form.due_date ? "opacity-40 pointer-events-none" : ""}>
                <TimePicker
                  testIdPrefix="task-due-time"
                  value={form.due_time || ""}
                  onChange={(v) => setForm({ ...form, due_time: v })}
                />
              </div>
              {form.due_time && form.due_date && (
                <button
                  type="button"
                  data-testid="task-due-time-clear"
                  onClick={() => setForm({ ...form, due_time: "" })}
                  className="text-[10px] font-mono uppercase tracking-wide text-zinc-500 hover:text-amber-400 self-start"
                  aria-label="Clear due time"
                >
                  ✕ Clear time
                </button>
              )}
              <p className="text-[10px] text-zinc-600 leading-tight">
                Flips to LATE the minute after this time on the due date. Requires a due date.
              </p>
            </div>
          </div>

          {/* Recurring weekly settings — the recurrence day-of-week is
              auto-derived from this task's own start / due dates, so all
              the user needs to decide is "should this repeat" and
              (optionally) "until when". */}
          {!form.is_milestone && (
            <div className="space-y-2 pt-3 border-t border-zinc-900">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="task-is-recurring"
                  checked={!!form.is_recurring}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      is_recurring: e.target.checked,
                      recurrence_type: e.target.checked ? "weekly" : null,
                      recurrence_end_date: e.target.checked
                        ? form.recurrence_end_date
                        : "",
                    })
                  }
                  className="w-4 h-4 accent-amber-500"
                />
                <span className="text-sm text-zinc-200">Repeat weekly</span>
                <span className="text-[11px] text-zinc-500 ml-2">
                  Uses this task&apos;s start / due days. Auto-creates each
                  Monday at 8am Central.
                </span>
              </label>
              {form.is_recurring && (
                <div className="pl-6 max-w-xs">
                  <Label className="text-overline text-zinc-500">
                    Repeat until <span className="text-zinc-600 normal-case">(optional)</span>
                  </Label>
                  <div className="relative mt-1.5">
                    <Input
                      data-testid="task-recurrence-end-date"
                      type="date"
                      value={form.recurrence_end_date || ""}
                      onChange={(e) =>
                        setForm({ ...form, recurrence_end_date: e.target.value })
                      }
                      className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                    />
                    {form.recurrence_end_date && (
                      <button
                        type="button"
                        data-testid="task-recurrence-end-clear"
                        onClick={() => setForm({ ...form, recurrence_end_date: "" })}
                        className="absolute right-9 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200 text-xs"
                        aria-label="Clear end date"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-zinc-600 leading-tight mt-1">
                    Blank = repeat indefinitely.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* OVERSHOOT banner — computed end walks past target due_date.
              Purely visual; scheduling math doesn't shrink the task, this
              just flags the planning drift so the user can react. */}
          {(() => {
            const os = overshootStatus(task);
            if (!os.overshoot) return null;
            return (
              <div
                data-testid="task-overshoot-banner"
                className="flex items-start gap-2 rounded-sm border border-rose-900/60 bg-rose-950/30 px-3 py-2 text-xs text-rose-200"
              >
                <AlertOctagon className="w-4 h-4 shrink-0 text-rose-400 mt-0.5" />
                <div className="min-w-0">
                  <div className="font-mono uppercase tracking-wide text-[10px] text-rose-300">
                    Overshoot · +{os.days}d
                  </div>
                  <div className="text-[11px] text-rose-200/80 leading-tight mt-0.5">
                    End date <span className="font-mono">{task?.end || "—"}</span>
                    {" "}walks past due date{" "}
                    <span className="font-mono">{task?.due_date || "—"}</span>.
                    Shorten the duration or push the due date to clear it.
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Status</Label>
              <Select
                value={form.kanban_status}
                onValueChange={(v) => setForm({ ...form, kanban_status: v })}
              >
                <SelectTrigger
                  data-testid="task-status"
                  className="bg-zinc-950 border-zinc-800 focus:ring-amber-500"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {KANBAN_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value} className="focus:bg-zinc-900">
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">% Complete</Label>
              <Input
                data-testid="task-percent"
                type="number"
                min={0}
                max={100}
                value={form.percent_complete}
                onChange={(e) => {
                  const pct = Math.min(100, Math.max(0, Number(e.target.value) || 0));
                  setForm({
                    ...form,
                    percent_complete: pct,
                    // Mirror the checkbox→percent sync: keep kanban_status
                    // aligned with the Percent field so the two controls
                    // never disagree in the dialog.
                    //   Typing 100 → auto-check "Mark complete"
                    //   Dropping below 100 while task was in "done" →
                    //     flip status back to "doing" so the checkbox
                    //     unticks (the task is no longer complete).
                    //   Any other change → leave status alone (a
                    //     partially-done task stays whatever it was set
                    //     to previously — todo / doing / blocked).
                    kanban_status:
                      pct === 100
                        ? "done"
                        : form.kanban_status === "done"
                        ? "doing"
                        : form.kanban_status,
                  });
                }}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 font-mono"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="milestone-toggle"
              data-testid="task-milestone"
              type="checkbox"
              checked={form.is_milestone}
              onChange={(e) => setForm({ ...form, is_milestone: e.target.checked })}
              className="accent-amber-500"
            />
            <Label htmlFor="milestone-toggle" className="text-sm text-zinc-300 cursor-pointer">
              Mark as milestone (zero-duration diamond)
            </Label>
          </div>

          {/* Assignee picker */}
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Assigned To</Label>
            <Select
              value={form.assigned_user_id || "__unassigned__"}
              onValueChange={(v) =>
                setForm({ ...form, assigned_user_id: v === "__unassigned__" ? "" : v })
              }
              disabled={isProdLinked}
            >
              <SelectTrigger
                data-testid="task-assignee"
                className="bg-zinc-950 border-zinc-800 focus:ring-amber-500"
              >
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="__unassigned__" className="focus:bg-zinc-900 text-zinc-500">
                  Unassigned
                </SelectItem>
                {assignableUsers.map((u) => (
                  <SelectItem
                    key={u.user_id}
                    value={u.user_id}
                    className="focus:bg-zinc-900"
                  >
                    {u.name || u.email}
                    {u.is_global_admin && (
                      <span className="ml-2 text-[10px] text-amber-500/70 uppercase">admin</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Capacity-aware assignment warning — surfaces this-week workload
                once the producer picks someone. Silent for healthy loads,
                amber for tight, rose for overloaded. Non-blocking. */}
            {form.assigned_user_id && assigneeCapacity && !capacityLoading && (() => {
              const util = assigneeCapacity.utilization || 0;
              const cap = assigneeCapacity.week_capacity || assigneeCapacity.capacity_hours || 0;
              const scheduled = assigneeCapacity.scheduled_hours || 0;
              const pct = Math.round(util * 100);
              const overloaded = util >= 1.0;
              const tight = !overloaded && util >= 0.85;
              if (!overloaded && !tight) return null;
              const tone = overloaded
                ? "border-rose-900/60 bg-rose-950/30 text-rose-200"
                : "border-amber-900/60 bg-amber-950/30 text-amber-200";
              const chipTone = overloaded
                ? "text-rose-300"
                : "text-amber-300";
              const firstName = (assigneeCapacity.name || "").split(" ")[0] || "This assignee";
              return (
                <div
                  data-testid="assignee-capacity-warning"
                  className={`mt-2 flex items-start gap-2 rounded-sm border ${tone} px-2.5 py-1.5 text-[11px] leading-tight`}
                >
                  <TrendingUp className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${chipTone}`} />
                  <div className="min-w-0">
                    <span className={`font-mono ${chipTone}`}>
                      {firstName} is at {Math.round(scheduled)}h/{cap}h this week ({pct}%)
                    </span>
                    <span className="text-zinc-400">
                      {" "}·{" "}
                      {overloaded
                        ? "consider reassigning or pushing the start date"
                        : "workload is tight — heads up"}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Dependencies — API-backed section when editing an existing
              task; local-state pending list when creating a new one. */}
          {isEdit && onCreateDependency && (
            <DependencySection
              task={task}
              allTasks={allTasks}
              dependencies={dependencies}
              onAdd={onCreateDependency}
              onRemove={onDeleteDependency}
            />
          )}
          {!isEdit && (
            <PendingDependencySection
              allTasks={allTasks}
              pending={pendingPredecessors}
              onAdd={(fromId, type) =>
                setPendingPredecessors((prev) => [
                  ...prev,
                  { from_id: fromId, type },
                ])
              }
              onRemove={(fromId) =>
                setPendingPredecessors((prev) =>
                  prev.filter((p) => p.from_id !== fromId),
                )
              }
            />
          )}

          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Notes</Label>
            <Textarea
              data-testid="task-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional details, dependencies, who's responsible…"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 min-h-[70px]"
            />
          </div>
          </fieldset>

          {/* Comments thread — only shown on existing tasks (new ones have no id yet) */}
          {isEdit && task?.id && (
            <TaskCommentThread taskId={task.id} projectId={projectId} canEdit={true} />
          )}

          <DialogFooter className="pt-2 flex sm:justify-between gap-2 flex-row">
            <div>
              {isEdit && canEdit && !isProdLinked && onDelete && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="task-delete-btn"
                      className="bg-transparent border-rose-900/60 text-rose-400 hover:bg-rose-950/40 hover:text-rose-300"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete &ldquo;{task?.name}&rdquo;?</AlertDialogTitle>
                      <AlertDialogDescription className="text-zinc-500">
                        This task and any dependency arrows that touch it will be removed.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900">
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="task-delete-confirm"
                        onClick={() => onDelete && onDelete(task)}
                        className="bg-rose-500 text-zinc-50 hover:bg-rose-600"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {!canEdit && (
                <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wide flex items-center gap-1.5">
                  · Read-only — your role doesn&apos;t allow edits
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
              >
                {canEdit ? "Cancel" : "Close"}
              </Button>
              {canEdit && (
                <Button
                  type="submit"
                  data-testid="task-save-btn"
                  disabled={saving}
                  className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
                >
                  {saving ? "Saving…" : isEdit ? "Save" : "Create"}
                </Button>
              )}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function emptyForm(defaultPhase) {
  return {
    name: "",
    phase: defaultPhase || "pre",
    start: todayISO(),
    start_time: "",
    duration: 1,
    is_milestone: false,
    schedule_mode: defaultPhase === "prod" ? "locked" : "cascade",
    kanban_status: "todo",
    percent_complete: 0,
    notes: "",
    assigned_user_id: "",
    due_date: "",
    due_time: "",
    is_recurring: false,
    recurrence_type: null,
    recurrence_end_date: "",
  };
}

// ─── Dependency editor (predecessor list with type selector) ─────────────
//
// The "Advanced types" toggle keeps FF/SF behind a click so the common case
// (FS / SS — easily 95% of real usage) doesn't bury the user in jargon.
// State is local per-dialog-open; this is a power-user reveal, not a setting.
const DEP_TYPE_OPTIONS = [
  { value: "FS", label: "FS · Finish → Start", hint: "Dependent starts after predecessor ends", advanced: false },
  { value: "SS", label: "SS · Start → Start",  hint: "Dependent starts when predecessor starts", advanced: false },
  { value: "FF", label: "FF · Finish → Finish", hint: "Dependent ends when predecessor ends",     advanced: true  },
  { value: "SF", label: "SF · Start → Finish",  hint: "Dependent ends when predecessor starts (rare)", advanced: true },
];

function DependencySection({ task, allTasks, dependencies, onAdd, onRemove }) {
  const [picker, setPicker] = useState("");
  const [pickerType, setPickerType] = useState("FS");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const predecessors = dependencies.filter((d) => d.to_id === task.id);
  const predIds = new Set(predecessors.map((d) => d.from_id));
  const candidates = (allTasks || []).filter(
    (t) => t.id !== task.id && !predIds.has(t.id),
  );

  // Auto-reveal Advanced if the user is editing a dep that already uses an
  // advanced type — they shouldn't have to click "Show advanced" just to see
  // their own data.
  const hasAdvancedInUse = predecessors.some(
    (d) => DEP_TYPE_OPTIONS.find((o) => o.value === (d.type || "FS"))?.advanced,
  );
  const advancedVisible = showAdvanced || hasAdvancedInUse;

  const visibleTypeOptions = advancedVisible
    ? DEP_TYPE_OPTIONS
    : DEP_TYPE_OPTIONS.filter((o) => !o.advanced);

  const handleAdd = async () => {
    if (!picker || !onAdd) return;
    await onAdd(picker, task.id, pickerType);
    setPicker("");
    setPickerType("FS");
  };

  return (
    <div className="space-y-2 pt-2 border-t border-zinc-900" data-testid="dependency-section">
      <Label className="text-overline text-zinc-500">Depends On</Label>
      {predecessors.length > 0 ? (
        <ul className="space-y-1.5">
          {predecessors.map((dep) => {
            const pred = (allTasks || []).find((t) => t.id === dep.from_id);
            return (
              <li
                key={dep.id}
                data-testid={`dep-row-${dep.id}`}
                className="flex items-center justify-between gap-2 text-sm rounded-sm bg-zinc-900/60 border border-zinc-800 px-3 py-1.5"
              >
                <span className="text-zinc-200 truncate flex-1">
                  {pred?.name || "Unknown task"}
                </span>
                <span
                  data-testid={`dep-type-${dep.id}`}
                  className="text-[10px] font-mono text-amber-500/90 uppercase px-1.5 py-0.5 rounded-sm bg-amber-500/10 border border-amber-500/30"
                  title={(DEP_TYPE_OPTIONS.find((o) => o.value === (dep.type || "FS")) || {}).hint}
                >
                  {dep.type || "FS"}
                </span>
                {onRemove && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid={`dep-remove-${dep.id}`}
                    onClick={() => onRemove(dep)}
                    className="h-7 w-7 p-0 text-zinc-500 hover:text-rose-400 hover:bg-rose-950/30"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="text-xs text-zinc-500 italic">
          No predecessors. Pick one below or drag from another bar in the Gantt.
        </div>
      )}
      {candidates.length > 0 && (
        <>
          <div className="flex gap-2">
            <Select value={picker} onValueChange={setPicker}>
              <SelectTrigger
                data-testid="dep-picker"
                className="flex-1 bg-zinc-950 border-zinc-800 focus:ring-amber-500"
              >
                <SelectValue placeholder="Add predecessor…" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {candidates.map((c) => (
                  <SelectItem key={c.id} value={c.id} className="focus:bg-zinc-900">
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={pickerType} onValueChange={setPickerType}>
              <SelectTrigger
                data-testid="dep-type-picker"
                className="w-24 bg-zinc-950 border-zinc-800 focus:ring-amber-500 font-mono"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {visibleTypeOptions.map((t) => (
                  <SelectItem key={t.value} value={t.value} className="focus:bg-zinc-900">
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              data-testid="dep-add-btn"
              onClick={handleAdd}
              disabled={!picker}
              className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm disabled:opacity-40"
            >
              Add
            </Button>
          </div>
          {!hasAdvancedInUse && (
            <button
              type="button"
              data-testid="dep-show-advanced"
              onClick={() => setShowAdvanced((v) => !v)}
              className="text-[11px] text-zinc-500 hover:text-amber-500 transition-colors"
            >
              {showAdvanced ? "− Hide advanced types" : "+ Show advanced types (FF, SF)"}
            </button>
          )}
        </>
      )}
    </div>
  );
}


// ─── Pending-dependency editor (create-mode only) ─────────────────────────
//
// Mirrors DependencySection but backed by local state instead of API calls,
// so a producer can attach predecessors on the same round-trip that creates
// the task. Once the task is saved, the payload is drained and the "real"
// DependencySection takes over on subsequent edits.
function PendingDependencySection({ allTasks, pending, onAdd, onRemove }) {
  const [picker, setPicker] = useState("");
  const [pickerType, setPickerType] = useState("FS");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const pendingIds = new Set(pending.map((p) => p.from_id));
  const candidates = (allTasks || []).filter((t) => !pendingIds.has(t.id));

  const visibleTypeOptions = showAdvanced
    ? DEP_TYPE_OPTIONS
    : DEP_TYPE_OPTIONS.filter((o) => !o.advanced);

  const handleAdd = () => {
    if (!picker) return;
    onAdd(picker, pickerType);
    setPicker("");
    setPickerType("FS");
  };

  return (
    <div
      className="space-y-2 pt-2 border-t border-zinc-900"
      data-testid="pending-dependency-section"
    >
      <Label className="text-overline text-zinc-500">Depends On</Label>
      {pending.length > 0 ? (
        <ul className="space-y-1.5">
          {pending.map((p) => {
            const pred = (allTasks || []).find((t) => t.id === p.from_id);
            return (
              <li
                key={p.from_id}
                data-testid={`pending-dep-row-${p.from_id}`}
                className="flex items-center justify-between gap-2 text-sm rounded-sm bg-zinc-900/60 border border-zinc-800 px-3 py-1.5"
              >
                <span className="text-zinc-200 truncate flex-1">
                  {pred?.name || "Unknown task"}
                </span>
                <span
                  className="text-[10px] font-mono text-amber-500/90 uppercase px-1.5 py-0.5 rounded-sm bg-amber-500/10 border border-amber-500/30"
                  title={
                    (DEP_TYPE_OPTIONS.find((o) => o.value === (p.type || "FS")) || {}).hint
                  }
                >
                  {p.type || "FS"}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid={`pending-dep-remove-${p.from_id}`}
                  onClick={() => onRemove(p.from_id)}
                  className="h-7 w-7 p-0 text-zinc-500 hover:text-rose-400 hover:bg-rose-950/30"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="text-xs text-zinc-500 italic">
          No predecessors. Add one below — they&apos;ll be linked when the task is created.
        </div>
      )}
      {candidates.length > 0 && (
        <>
          <div className="flex gap-2">
            <Select value={picker} onValueChange={setPicker}>
              <SelectTrigger
                data-testid="pending-dep-picker"
                className="flex-1 bg-zinc-950 border-zinc-800 focus:ring-amber-500"
              >
                <SelectValue placeholder="Add predecessor…" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {candidates.map((c) => (
                  <SelectItem key={c.id} value={c.id} className="focus:bg-zinc-900">
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={pickerType} onValueChange={setPickerType}>
              <SelectTrigger
                data-testid="pending-dep-type-picker"
                className="w-24 bg-zinc-950 border-zinc-800 focus:ring-amber-500 font-mono"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {visibleTypeOptions.map((t) => (
                  <SelectItem key={t.value} value={t.value} className="focus:bg-zinc-900">
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              data-testid="pending-dep-add-btn"
              onClick={handleAdd}
              disabled={!picker}
              className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm disabled:opacity-40"
            >
              Add
            </Button>
          </div>
          <button
            type="button"
            data-testid="pending-dep-show-advanced"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-[11px] text-zinc-500 hover:text-amber-500 transition-colors"
          >
            {showAdvanced ? "− Hide advanced types" : "+ Show advanced types (FF, SF)"}
          </button>
        </>
      )}
    </div>
  );
}
